import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FolderOpen, FileText, Image, Video, File, Download, Lock,
  Layout, Grid, BookOpen, DollarSign, Presentation, FileSignature,
  RefreshCw, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_API = `${API}/project-catalog`;

// Category icons
const categoryIcons = {
  floor_plan: Layout,
  fact_sheet: FileText,
  unit_plan: Grid,
  brochure: BookOpen,
  price_list: DollarSign,
  site_photo: Image,
  video: Video,
  presentation: Presentation,
  contract: FileSignature,
  other: File,
};

// Category colors
const categoryColors = {
  floor_plan: 'bg-blue-500',
  fact_sheet: 'bg-green-500',
  unit_plan: 'bg-purple-500',
  brochure: 'bg-orange-500',
  price_list: 'bg-yellow-500',
  site_photo: 'bg-pink-500',
  video: 'bg-red-500',
  presentation: 'bg-indigo-500',
  contract: 'bg-slate-500',
  other: 'bg-gray-500',
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    timeZone: 'Asia/Dubai',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function SharedProjectView() {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  
  // Group documents by category
  const groupedDocuments = documents.reduce((acc, doc) => {
    const category = doc.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(doc);
    return acc;
  }, {});
  
  // Fetch shared project
  const fetchSharedProject = async (pwd = null) => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${PROJECT_API}/shared/${shareToken}`;
      if (pwd) {
        url += `?password=${encodeURIComponent(pwd)}`;
      }
      
      const response = await axios.get(url);
      
      if (response.data.success) {
        setProject(response.data.project);
        setDocuments(response.data.documents || []);
        setNeedsPassword(false);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setNeedsPassword(true);
      } else if (err.response?.status === 404) {
        setError('This share link is invalid or has expired.');
      } else {
        setError('Failed to load shared project. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (shareToken) {
      fetchSharedProject();
    }
  }, [shareToken]);
  
  // Handle password submit
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password) {
      fetchSharedProject(password);
    }
  };
  
  // Download document
  const handleDownload = async (doc) => {
    try {
      let url = `${PROJECT_API}/shared/${shareToken}/documents/${doc.id}/download`;
      if (password) {
        url += `?password=${encodeURIComponent(password)}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Check if response is actually an error (JSON) instead of file
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      }
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${doc.file_name}`);
    } catch (err) {
      console.error('Download error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to download document';
      toast.error(errorMessage);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-accent mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Loading shared project...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Unable to Access
            </h2>
            <p className="text-slate-600 dark:text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Password required state
  if (needsPassword) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-accent" />
            </div>
            <CardTitle>Password Protected</CardTitle>
            <CardDescription>
              This shared project is password protected. Please enter the password to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              <Button type="submit" className="w-full" disabled={!password}>
                Access Project
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main content
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center">
              <FolderOpen className="w-7 h-7 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {project?.name}
              </h1>
              {project?.description && (
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  {project.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
            <span>{documents.length} documents</span>
            <span>•</span>
            <span>Shared on {formatDate(project?.created_at)}</span>
          </div>
        </div>
      </div>
      
      {/* Documents */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {documents.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Documents</h3>
              <p className="text-slate-500">This project doesn't have any documents yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedDocuments).map(([category, docs]) => {
              const Icon = categoryIcons[category] || File;
              const categoryName = category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              
              return (
                <div key={category}>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${categoryColors[category] || 'bg-slate-500'} flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    {categoryName}
                    <Badge variant="secondary">{docs.length}</Badge>
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {docs.map((doc) => (
                      <Card key={doc.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-lg ${categoryColors[doc.category] || 'bg-slate-500'} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-slate-900 dark:text-white truncate">
                                {doc.file_name}
                              </h3>
                              <p className="text-sm text-slate-500 mt-1">
                                {formatFileSize(doc.file_size)}
                              </p>
                              {doc.description && (
                                <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                                  {doc.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-4"
                            onClick={() => handleDownload(doc)}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="bg-slate-100 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 mt-8">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-slate-500">
            Shared via Treppan CRM
          </p>
        </div>
      </div>
    </div>
  );
}
